import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import Avatar from '../avatar/Avatar';
import TypeChip from '../chip/TypeChip';
import SimulatorAvatar from '../simulator-avatar/SimulatorAvatar';
import { ExerciseType, SimulationStatus, SimulatorType } from '../types';
import { useDesktop } from '../useDesktop';
import { reLabelExerciseType } from '../utils';
import { formatDateTime, formatDuration, formatTime } from '../utils/formatTime';
import SimulationProperty from './SimulationProperty';

export interface SimulationCardProps {
  adHoc: boolean;
  endTime?: Date;
  exerciseName: string;
  exerciseType: ExerciseType;
  position?: string;
  simulatorType: SimulatorType;
  startTime: Date;
  status?: SimulationStatus;
  studentName?: string;
  studentAvatar?: string;
}

const SimulationCard = ({
  adHoc,
  endTime,
  exerciseName,
  exerciseType,
  position,
  simulatorType,
  startTime,
  status,
  studentAvatar,
  studentName,
}: SimulationCardProps) => {
  const isDesktop = useDesktop();
  const Wrapper = props => (isDesktop ? <Card {...props} /> : <Box {...props} />);
  const InnerWrapper = props => (isDesktop ? <CardContent {...props} /> : <Box {...props} />);

  return (
    <Wrapper sx={{ backgroundColor: theme => theme.palette.background.default, mt: 4, mb: 3 }}>
      <InnerWrapper sx={{ display: 'flex', gap: 3 }}>
        <SimulatorAvatar
          adHoc={adHoc}
          exerciseType={exerciseType}
          simulatorType={simulatorType}
          sx={{ display: { xs: 'none', sm: 'grid' }, height: 160, width: 160 }}
        />

        <Box flex="1" display="flex" flexDirection="column" position="relative">
          {status && (
            <TypeChip
              sx={{ position: 'absolute', right: 0, top: 0 }}
              type={status}
              icon={
                status === SimulationStatus.Ongoing ? (
                  <CircularProgress size={14} sx={{ color: 'inherit' }} />
                ) : undefined
              }
            />
          )}
          <Typography variant="overline" sx={{ display: { xs: 'none', sm: 'block' }, my: 1.5 }}>
            Simulation
          </Typography>
          <Typography component="h1" variant="h4" mb={{ xs: 4, sm: 1 }}>
            {reLabelExerciseType(exerciseType)}
          </Typography>
          <Typography color="primary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {exerciseName}
          </Typography>

          <Box
            sx={{
              mt: 'auto',
              mb: { xs: 2, sm: 0 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              // flexWrap: 'wrap', Enable if we don't want to wrap labels and values
              columnGap: 2,
              rowGap: { xs: 1, sm: 0 },
            }}
          >
            {studentName && (
              <Box display="flex" alignItems="center" gap={1} whiteSpace="nowrap">
                <Avatar src={studentAvatar} /> <Typography component="span">{studentName}</Typography>
              </Box>
            )}
            <SimulationProperty
              name="Start"
              value={studentName ? formatTime(startTime, { timeStyle: 'medium' }) : formatDateTime(startTime)}
            />
            <SimulationProperty
              name="End"
              value={
                endTime ? (studentName ? formatTime(endTime, { timeStyle: 'medium' }) : formatDateTime(endTime)) : '-'
              }
            />
            <SimulationProperty
              name="Duration"
              value={startTime && endTime ? formatDuration(startTime, endTime) : '-'}
            />
            {position && <SimulationProperty name="Environment" value={position} />}
          </Box>
        </Box>
      </InnerWrapper>
    </Wrapper>
  );
};

export default SimulationCard;
